// Explicit product events go to our own backend. No DOM capture, URLs,
// account identifiers, input values, replay, or third-party browser SDK.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const backend = window.MJ_BACKEND || "";
const platform = ["ios", "android"].includes(window.MJ_PLATFORM) ? window.MJ_PLATFORM : "web";
const path = location.pathname;
const screen = /\/host(?:\.html)?\/?$/.test(path) ? "host"
  : /\/(?:player(?:\.html)?|play\/[^/]+|[A-Za-z0-9]{4})\/?$/.test(path) ? "player"
  : path === "/" || path === "/landing.html" ? "home" : "other";
let installationId = null;
let enabled = false;
let lastActiveDay = "";
const pending = [];
const STORE_ENTRY_POINTS = new Set(["main_menu", "free_play", "custom_questions", "balance", "other"]);
const STORE_PLANS = new Set(["credits1", "credits5", "credits20", "weekly_unlimited"]);
const STORE_PROVIDERS = new Set(["apple", "google", "moyasar", "stub"]);
const STORE_FAILURE_REASONS = new Set([
  "account_binding", "delivery_pending", "initialization", "network",
  "rejected", "sign_in_required", "unavailable", "unknown",
]);

function safeProperties(event, supplied) {
  const source = supplied && typeof supplied === "object" ? supplied : {};
  const properties = {};
  if (event === "store opened") {
    if (STORE_ENTRY_POINTS.has(source.entry_point)) properties.entry_point = source.entry_point;
    if (typeof source.signed_in === "boolean") properties.signed_in = source.signed_in;
  }
  if (event === "store plan selected" || event.startsWith("store checkout ")) {
    if (STORE_PLANS.has(source.plan_id)) properties.plan_id = source.plan_id;
  }
  if (event.startsWith("store checkout ") || event.startsWith("store restore ")) {
    if (STORE_PROVIDERS.has(source.provider)) properties.provider = source.provider;
  }
  if (event === "store checkout failed" || event === "store restore failed") {
    if (STORE_FAILURE_REASONS.has(source.failure_reason)) {
      properties.failure_reason = source.failure_reason;
    }
  }
  return properties;
}

function isInternal() {
  try { return localStorage.getItem("mj_analytics_internal") === "1"; } catch { return false; }
}

function identity() {
  try {
    if (navigator.globalPrivacyControl || navigator.doNotTrack === "1"
        || localStorage.getItem("mj_analytics_opt_out") === "1" || isInternal()) return null;
    if (!installationId) {
      const stored = localStorage.getItem("mj_analytics_installation");
      installationId = UUID.test(stored || "") ? stored : crypto.randomUUID();
      localStorage.setItem("mj_analytics_installation", installationId);
    }
    return installationId;
  } catch { return null; }
}

function send(event, properties = {}) {
  const id = identity();
  if (!id) return;
  if (!enabled) {
    if (pending.length < 20) pending.push({ event, properties });
    return;
  }
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);
    fetch(`${backend}/pg-api/analytics`, {
      method: "POST", credentials: "omit", referrerPolicy: "no-referrer",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event, installationId: id, screen, platform, properties }),
      signal: controller.signal, keepalive: true,
    }).catch(() => {}).finally(() => clearTimeout(timeout));
  } catch { /* Storage/network failures must not reach gameplay. */ }
}

function activeDay() {
  if (!enabled || document.visibilityState === "hidden") return;
  const day = new Date().toISOString().slice(0, 10);
  if (day === lastActiveDay) return;
  lastActiveDay = day;
  send("app active");
}

window.MJAnalytics = {
  configure(value) {
    enabled = value === true;
    if (!enabled) { pending.length = 0; return; }
    send("app opened");
    activeDay();
    pending.splice(0).forEach((item) => send(item.event, item.properties));
  },
  attachSocket(socket) {
    if (!socket?.on) return;
    socket.on("analytics:internal", () => window.MJAnalytics?.setInternal());
    socket.on("connect", () => {
      // Owner devices stay excluded while signed out, including rooms they host.
      if (isInternal()) return socket.emit("analytics:hello", { internal: true, platform });
      const id = identity();
      if (id) socket.emit("analytics:hello", { installationId: id, platform });
    });
  },
  setInternal() {
    try { localStorage.setItem("mj_analytics_internal", "1"); } catch {}
    pending.length = 0;
    installationId = null;
  },
  track(event, properties = {}) {
    const allowed = [
      "sign in requested", "sign in unavailable",
      "store opened", "store plan selected", "store history opened",
      "store checkout started", "store checkout completed", "store checkout pending",
      "store checkout cancelled", "store checkout failed",
      "store restore started", "store restore completed", "store restore failed",
    ];
    if (allowed.includes(event)) send(event, safeProperties(event, properties));
  },
};
document.addEventListener("visibilitychange", activeDay);
window.addEventListener("mj-app-active", activeDay);
// A foreground app left open over midnight counts on the next day too.
setInterval(activeDay, 60_000);
